import { Box } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { getIntegrations } from '../../../../features/settings/integrations';
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import { Integration } from '../../../../features/settings/integrations/types';
import { useIntl } from 'react-intl';
import ZohoIntegrationBanner from './components/integrations/zoho/banner'
import { connectIntegration, disconnectIntegration } from './components/integrations/functions';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { DeleteDialog } from '../../../../components/shared/delete-dialog';
import { NAVIGATION_ROUTES } from '../../../../routes/NavigationRoute';
import { Lx } from '../../../../i18n/consts';
import TabLayout from '../../../../components/shared/tab-layout';
import SettingsHeader from '../settings-header';
import { canManageIntegrations } from '../../../../features/profile/entityPermissions';
import { NAVIGATION_SECTION_VISIBILITY } from '../../../../features/profile/navigationVisibility';
import { isSectionVisible } from '../../../../helpers/permissionHelper';

export default function Integrations() {
    const intl = useIntl();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const profile = useAppSelector(store => store.profile);
    const integrations = useAppSelector(store => store.integrations);
    const [loading, setLoading] = useState<boolean>(true);
    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [integrationToDelete, setIntegrationToDelete] = useState<Integration | null>(null);
    const isMob = useMediaQuery({ maxWidth: '1000px' });
    const canViewIntegrations = isSectionVisible(profile, NAVIGATION_SECTION_VISIBILITY.settingsIntegrations);
    const canEditIntegrations = canManageIntegrations(profile);

    const fetchData = () => {
        setLoading(true);
        dispatch(getIntegrations()).then(() => {
            setLoading(false);
        });
    }

    const handleIntegrationConnectClick = (integration: Integration) => {
        if (!canEditIntegrations || integration.enabled) {
            return
        }

        connectIntegration(dispatch, integration)
    }

    const handleIntegrationDisconnectClick = (integration: Integration) => {
        if (!canEditIntegrations || !integration.enabled) {
            return
        }

        setIntegrationToDelete(integration);
        setDeleteDialogOpen(true);
    }

    const handleIntegrationConfigureClick = (integration: Integration) => {
        if (!canEditIntegrations) {
            return;
        }

        navigate(NAVIGATION_ROUTES.SETTINGS_INTEGRATIONS_ZOHO_EDIT, {
            state: { integration }
        });
    }

    const handleIntegrationDisconnectConfirm = async () => {
        if (!integrationToDelete) {
            return;
        }

        await dispatch(disconnectIntegration(integrationToDelete));
        setDeleteDialogOpen(false);
        setIntegrationToDelete(null);
        fetchData();
    }

    const handleIntegrationDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setIntegrationToDelete(null);
    }

    useEffect(() => {
        if (!canViewIntegrations) {
            navigate(NAVIGATION_ROUTES.SETTINGS, { replace: true });
            return;
        }

        fetchData();
    }, [canViewIntegrations, dispatch, navigate]);

    if (!canViewIntegrations) {
        return null;
    }

    return (
        <TabLayout header={<SettingsHeader activeTab={'integrations'} loading={loading} />}>
            <Box sx={{ padding: isMob ? '12px' : '0 32px 16px', width: '100%', alignSelf: 'stretch' }}>
                {
                    !loading && integrations.items.length > 0 ? integrations.items.map((integration, index) => {
                        if (integration.id == 'zoho') {
                            return <ZohoIntegrationBanner key={index} integration={integration}
                                canManage={canEditIntegrations}
                                onConnectClick={handleIntegrationConnectClick}
                                onConfigureClick={handleIntegrationConfigureClick}
                                onDisconnectClick={handleIntegrationDisconnectClick} />
                        } else {
                            return <span key={index}></span>
                        }
                    })
                        : !loading && intl.formatMessage({ id: Lx.Integration.NONE_AVAILABLE })
                }
            </Box>
            
            <DeleteDialog
                open={isDeleteDialogOpen}
                onCancel={handleIntegrationDeleteCancel}
                onConfirmDelete={handleIntegrationDisconnectConfirm}
                deleteHeader={intl.formatMessage({ id: Lx.Integration.DISCONNECT_TITLE_FORMAT })}
                deleteMessage={intl.formatMessage({ id: Lx.Integration.DISCONNECT_DESC_FORMAT })}
                title={integrationToDelete?.name ?? intl.formatMessage({ id: Lx.General.INTEGRATIONS })}
                idDeleteButtonLocalization={Lx.Integration.DISCONNECT}
            />
        </TabLayout>
    )
}
